import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useLang } from "@/i18n/LangContext";
import { Gamepad2, KeyRound, CheckCircle2, Activity } from "lucide-react";

const Stat = ({ icon: Icon, label, value, accent, pulse, testid }) => (
  <div className="bg-[#0c0c0c] border border-white/5 rounded-xl p-6 relative overflow-hidden" data-testid={testid}>
    {pulse && (
      <span className="absolute top-4 end-4 flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-green opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-green"></span>
      </span>
    )}
    <div className={`w-11 h-11 rounded-lg ${accent} flex items-center justify-center mb-4`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div className="font-heading text-4xl font-bold tabular-nums">{value ?? "—"}</div>
    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-2 font-bold">{label}</div>
  </div>
);

export default function Dashboard() {
  const { t } = useLang();
  const { data: stats } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/logs/dashboard")).data,
    refetchInterval: 15000,
  });
  const { data: deliveries = [] } = useQuery({
    queryKey: ["recent-deliveries"],
    queryFn: async () => (await api.get("/logs/deliveries?limit=10")).data,
    refetchInterval: 20000,
  });

  return (
    <div className="p-8" data-testid="admin-dashboard">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-2">Live Overview · auto-refresh</div>
        <h1 className="font-heading text-3xl font-bold">{t("admin.dashboard")}</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Stat icon={Activity} label="Online Now" value={stats?.online_users} accent="bg-brand-green/80" pulse testid="stat-online-now" />
        <Stat icon={Gamepad2} label="Games" value={stats?.games_total} accent="bg-brand/80" testid="stat-games" />
        <Stat icon={KeyRound} label="Available Keys" value={stats?.keys_available} accent="bg-emerald-600" testid="stat-keys-available" />
        <Stat icon={CheckCircle2} label="Used Keys" value={stats?.keys_used} accent="bg-blue-600" testid="stat-keys-used" />
      </div>

      <div className="bg-[#0c0c0c] border border-white/5 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/5">
          <h2 className="font-heading font-semibold">Recent Redemptions</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-start px-6 py-3 font-semibold">User</th>
                <th className="text-start px-6 py-3 font-semibold">Game</th>
                <th className="text-start px-6 py-3 font-semibold">Platform</th>
                <th className="text-start px-6 py-3 font-semibold">Key</th>
                <th className="text-start px-6 py-3 font-semibold">Time</th>
              </tr>
            </thead>
            <tbody>
              {deliveries.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-zinc-500 py-12">{t("common.empty")}</td></tr>
              ) : (
                deliveries.map((d) => (
                  <tr key={d.id} className="border-t border-white/5">
                    <td className="px-6 py-3">{d.user?.username || "—"}</td>
                    <td className="px-6 py-3">{d.product_title}</td>
                    <td className="px-6 py-3"><span className="text-xs px-2 py-0.5 rounded bg-brand-cyan/10 text-brand-cyan border border-brand-cyan/30">{d.product_platform}</span></td>
                    <td className="px-6 py-3 font-mono text-xs">{d.key_code}</td>
                    <td className="px-6 py-3 text-zinc-400 text-xs">{new Date(d.created_at).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
